import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Wifi, Shield, Phone, Tv, Network, Zap, CheckCircle2, Clock, Award, ArrowRight, Users, TrendingUp, Globe, Headphones, Star, Sparkles, WifiOff, AlertCircle, X, MapPin, Quote, Building, Home as HomeIcon, Briefcase, BadgeCheck, ThumbsUp } from 'lucide-react';
import AnimatedHero from '../components/AnimatedHero';
import { DiyaRow } from '../components/diwali/Diyas';
import { isDiwaliPeriod } from '../components/diwali/DiwaliTheme';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const Home = () => {
  const navigate = useNavigate();

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 800,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    fade: true,
    pauseOnHover: true,
    arrows: true
  };

  const sliderImages = [
    {
      url: 'https://images.unsplash.com/photo-1597733336794-12d05021d510?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxmaWJlciUyMGludGVybmV0fGVufDB8fHx8MTc2MjIwNTA2M3ww&ixlib=rb-4.1.0&q=85',
      title: 'Lightning-Fast Fiber Internet',
      subtitle: 'Experience speeds up to 1 Gbps'
    },
    {
      url: 'https://images.unsplash.com/photo-1665848383782-1ea74efde68f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxDQ1RWJTIwc3VydmVpbGxhbmNlfGVufDB8fHx8MTc2MjIwNTA2OHww&ixlib=rb-4.1.0&q=85',
      title: 'Advanced CCTV Security',
      subtitle: 'Keep your property safe 24/7'
    },
    {
      url: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxuZXR3b3JrJTIwc2VydmVyfGVufDB8fHx8MTc2MjIwNTA3OHww&ixlib=rb-4.1.0&q=85',
      title: 'Enterprise Networking Solutions',
      subtitle: 'Scalable infrastructure for businesses'
    },
    {
      url: 'https://images.pexels.com/photos/1181316/pexels-photo-1181316.jpeg',
      title: 'VoIP Communication Services',
      subtitle: 'Crystal-clear business calling'
    },
    {
      url: 'https://images.unsplash.com/photo-1604869515882-4d10fa4b0492?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxmaWJlciUyMGludGVybmV0fGVufDB8fHx8MTc2MjIwNTA2M3ww&ixlib=rb-4.1.0&q=85',
      title: 'Reliable Infrastructure',
      subtitle: '99.9% uptime guarantee'
    },
    {
      url: 'https://images.unsplash.com/photo-1690108114789-c9123dfe5b1b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHxDQ1RWJTIwc3VydmVpbGxhbmNlfGVufDB8fHx8MTc2MjIwNTA2OHww&ixlib=rb-4.1.0&q=85',
      title: 'Complete Surveillance Solutions',
      subtitle: 'Advanced security systems'
    }
  ];

  const services = [
    {
      id: 'internet-leased-line',
      icon: <Network className="h-12 w-12" />,
      title: 'Internet Leased Line',
      description: 'Dedicated, symmetric bandwidth with guaranteed speeds. Perfect for businesses requiring consistent, high-performance connectivity with SLA-backed uptime.',
      features: ['Dedicated bandwidth', 'Symmetric upload/download', 'SLA guarantee', '99.9% uptime'],
      image: 'https://images.unsplash.com/photo-1551703599-2a53f65da7e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxmaWJlciUyMGludGVybmV0fGVufDB8fHx8MTc2MjIwNTA2M3ww&ixlib=rb-4.1.0&q=85',
      gradient: 'from-red-600 via-rose-600 to-red-700',
      shadowColor: 'shadow-red-200'
    },
    {
      id: 'broadband-services',
      icon: <Wifi className="h-10 w-10" />,
      title: 'Broadband Services',
      description: 'High-speed residential and commercial broadband plans with unlimited data. Stream, game, and work from home without interruptions.',
      features: ['Up to 1 Gbps speed', 'Unlimited data', 'No FUP limits', 'Free installation'],
      image: 'https://images.unsplash.com/photo-1604869515882-4d10fa4b0492?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxmaWJlciUyMGludGVybmV0fGVufDB8fHx8MTc2MjIwNTA2M3ww&ixlib=rb-4.1.0&q=85',
      gradient: 'from-pink-600 via-rose-600 to-pink-700',
      shadowColor: 'shadow-pink-200'
    },
    {
      id: 'ott-services',
      icon: <Tv className="h-10 w-10" />,
      title: 'OTT Services',
      description: 'Seamless streaming experience with optimized connectivity for all major OTT platforms. Enjoy 4K content without buffering.',
      features: ['4K streaming support', 'Zero buffering', 'Multi-device support', 'Low latency'],
      image: 'https://images.pexels.com/photos/1148820/pexels-photo-1148820.jpeg',
      gradient: 'from-blue-600 via-indigo-600 to-blue-700',
      shadowColor: 'shadow-blue-200'
    },
    {
      id: 'voip-services',
      icon: <Phone className="h-10 w-10" />,
      title: 'VoIP Services',
      description: 'Enterprise-grade Voice over IP solutions with crystal-clear call quality. Reduce communication costs while improving reliability.',
      features: ['HD voice quality', 'Call recording', 'Virtual numbers', 'Cost-effective'],
      image: 'https://images.pexels.com/photos/1181316/pexels-photo-1181316.jpeg',
      gradient: 'from-yellow-600 via-amber-600 to-yellow-700',
      shadowColor: 'shadow-yellow-200'
    }
  ];

  const whyChooseUs = [
    {
      icon: <Zap className="h-8 w-8" />,
      title: 'Lightning Fast',
      description: 'Experience blazing-fast speeds up to 1 Gbps'
    },
    {
      icon: <Shield className="h-8 w-8" />,
      title: 'Secure & Reliable',
      description: '99.9% uptime with enterprise-grade security'
    },
    {
      icon: <Clock className="h-8 w-8" />,
      title: '24/7 Support',
      description: 'Round-the-clock technical assistance'
    },
    {
      icon: <Award className="h-8 w-8" />,
      title: 'Award Winning',
      description: 'Trusted by thousands of customers across India'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* New Animated Hero Section */}
      <AnimatedHero />
      {isDiwaliPeriod() && <DiyaRow count={7} size={44} className="bg-[#0B1F4D]" />}
      {/* OLD SLIDER SECTION - Keeping commented for reference
      <section className="relative">
        <div className="slider-container" style={{ maxHeight: '650px' }}>
          <Slider {...sliderSettings}>
            {sliderImages.map((slide, index) => (
              <div key={index}>
                <div className="relative h-[650px]">
                  <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ 
                      backgroundImage: `url(${slide.url})`,
                      filter: 'brightness(0.6)'
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent" />
                  
                  <div className="relative h-full flex items-center justify-center text-center px-6">
                    <div className="max-w-4xl">
                      <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 drop-shadow-2xl">
                        {slide.title}
                      </h1>
                      <p className="text-2xl md:text-3xl text-white mb-8 drop-shadow-lg">
                        {slide.subtitle}
                      </p>
                      <Button
                        onClick={() => navigate('/signup')}
                        className="h-16 px-10 text-xl font-bold text-white border-3 border-yellow-400 shadow-2xl hover:scale-105 transition-transform"
                        style={{ background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 100%)' }}
                      >
                        Get Started Today
                        <ArrowRight className="ml-3 h-6 w-6" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </Slider>
        </div>
      </section>
      END OLD SLIDER SECTION */}

      {/* Company Overview - Compact */}
      <section className="py-12 bg-gradient-to-br from-red-50 via-pink-50 to-blue-50 relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-5 w-48 h-48 bg-red-400 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 right-5 w-64 h-64 bg-blue-400 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-10">
            <div className="inline-block mb-3 px-4 py-1.5 bg-gradient-to-r from-red-500 to-pink-600 rounded-full text-white font-bold text-xs shadow-lg animate-bounce">
              <Sparkles className="inline h-3 w-3 mr-1" />
              India's Fastest Growing ISP
            </div>
            <h2 className="text-4xl font-black mb-4 bg-gradient-to-r from-red-600 via-pink-600 to-blue-700 bg-clip-text text-transparent">
              Welcome to Netix Fibernet
            </h2>
            <p className="text-base text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Your trusted partner for high-speed internet, enterprise networking, and communication solutions. 
              We deliver cutting-edge technology and unmatched service across India.
            </p>
          </div>

          {/* Stats - Compact */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="text-center p-4 border-0 shadow-lg bg-gradient-to-br from-red-50 to-red-100 hover:scale-105 transition-all duration-300 group">
              <CardContent className="p-0">
                <div className="text-3xl font-black mb-2 text-red-600 group-hover:scale-110 transition-transform">
                  1000+
                </div>
                <p className="text-gray-700 font-bold text-sm">Active Customers</p>
                <Users className="h-5 w-5 mx-auto mt-2 text-red-600" />
              </CardContent>
            </Card>
            <Card className="text-center p-4 border-0 shadow-lg bg-gradient-to-br from-pink-50 to-pink-100 hover:scale-105 transition-all duration-300 group">
              <CardContent className="p-0">
                <div className="text-3xl font-black mb-2 text-pink-600 group-hover:scale-110 transition-transform">
                  99.9%
                </div>
                <p className="text-gray-700 font-bold text-sm">Uptime Guarantee</p>
                <Award className="h-5 w-5 mx-auto mt-2 text-pink-600" />
              </CardContent>
            </Card>
            <Card className="text-center p-4 border-0 shadow-lg bg-gradient-to-br from-blue-50 to-blue-100 hover:scale-105 transition-all duration-300 group">
              <CardContent className="p-0">
                <div className="text-3xl font-black mb-2 text-blue-600 group-hover:scale-110 transition-transform">
                  24/7
                </div>
                <p className="text-gray-700 font-bold text-sm">Customer Support</p>
                <Headphones className="h-5 w-5 mx-auto mt-2 text-blue-600" />
              </CardContent>
            </Card>
            <Card className="text-center p-4 border-0 shadow-lg bg-gradient-to-br from-yellow-50 to-amber-100 hover:scale-105 transition-all duration-300 group">
              <CardContent className="p-0">
                <div className="text-3xl font-black mb-2 text-amber-600 group-hover:scale-110 transition-transform">
                  1 Gbps
                </div>
                <p className="text-gray-700 font-bold text-sm">Maximum Speed</p>
                <Zap className="h-5 w-5 mx-auto mt-2 text-amber-600" />
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Services - Compact */}
      <section className="py-12 bg-white relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="inline-block mb-2 px-3 py-1 bg-red-100 rounded-full text-red-700 font-bold text-xs">
              <Network className="inline h-3 w-3 mr-1" />
              What We Offer
            </div>
            <h2 className="text-3xl font-black mb-3 bg-gradient-to-r from-red-600 via-pink-600 to-blue-700 bg-clip-text text-transparent">
              Our Services
            </h2>
            <p className="text-sm text-gray-700 max-w-2xl mx-auto">
              Comprehensive connectivity and communication solutions tailored for your needs
            </p>
          </div>

          <div className="space-y-10">
            {services.map((service, index) => (
              <div key={index} className={`grid md:grid-cols-2 gap-6 items-center ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
                <div className={`${index % 2 === 1 ? 'md:order-2' : ''}`}>
                  <Card className={`border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-white hover:scale-102 ${service.shadowColor} group`}>
                    <CardContent className="p-5">
                      <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-all duration-300`}>
                        <div className="text-white">
                          {service.icon}
                        </div>
                      </div>
                      <h3 className="text-2xl font-black mb-3 text-gray-800">{service.title}</h3>
                      <p className="text-sm text-gray-700 mb-4 leading-relaxed">{service.description}</p>
                      
                      <div className="space-y-2 mb-4">
                        {service.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors">
                            <div className={`bg-gradient-to-br ${service.gradient} p-1 rounded-full`}>
                              <CheckCircle2 className="h-4 w-4 text-white flex-shrink-0" />
                            </div>
                            <span className="text-gray-800 font-semibold text-sm">{feature}</span>
                          </div>
                        ))}
                      </div>

                      <Button
                        onClick={() => navigate(`/service/${service.id}`)}
                        className={`w-full h-12 text-sm font-bold text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 bg-gradient-to-r ${service.gradient}`}
                      >
                        Learn More
                        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </CardContent>
                  </Card>
                </div>
                <div className={`${index % 2 === 1 ? 'md:order-1' : ''} relative group`}>
                  <div className="absolute -inset-0.5 bg-gradient-to-r ${service.gradient} rounded-2xl blur-lg opacity-40 group-hover:opacity-60 transition-opacity"></div>
                  <img
                    src={service.image}
                    alt={service.title}
                    className="relative rounded-2xl shadow-lg w-full h-[300px] object-cover border-2 border-white group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us - Compact */}
      <section className="py-12 bg-gradient-to-br from-red-900 via-pink-900 to-blue-900 relative overflow-hidden">
        {/* Animated Stars Background */}
        <div className="absolute inset-0 opacity-10">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="absolute w-0.5 h-0.5 bg-white rounded-full animate-pulse"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${1 + Math.random() * 98}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 3}s`
              }}
            />
          ))}
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-10">
            <div className="inline-block mb-2 px-3 py-1 bg-red-500/20 backdrop-blur-sm rounded-full text-red-200 font-bold text-xs border border-red-400/30">
              <Star className="inline h-3 w-3 mr-1" />
              Our Advantage
            </div>
            <h2 className="text-3xl font-black mb-3 text-white">
              Why Choose Netix Fibernet?
            </h2>
            <p className="text-sm text-pink-100 max-w-2xl mx-auto">
              Experience the difference with India's most trusted internet service provider
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {whyChooseUs.map((item, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-red-500/30 transition-all duration-300 bg-gradient-to-br from-red-800 to-pink-900 hover:scale-105 group">
                <CardContent className="p-5 text-center">
                  <div className="w-16 h-16 rounded-xl flex items-center justify-center mb-4 mx-auto bg-gradient-to-br from-red-500 to-pink-600 shadow-md group-hover:scale-110 transition-all duration-300">
                    <div className="text-white">
                      {item.icon}
                    </div>
                  </div>
                  <h3 className="text-lg font-black text-white mb-2">{item.title}</h3>
                  <p className="text-pink-100 text-sm leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Compact */}
      <section className="py-16 bg-gradient-to-br from-red-600 via-pink-700 to-blue-800 relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-15">
          <div className="absolute top-0 left-0 w-64 h-64 bg-red-400 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-blue-400 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        
        <div className="max-w-5xl mx-auto px-6 text-center relative z-10">
          <div className="inline-block mb-4 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white font-bold text-xs border-2 border-white/30 animate-bounce">
            <Sparkles className="inline h-4 w-4 mr-1" />
            Limited Time Offer - Free Installation!
          </div>
          <h2 className="text-4xl font-black text-white mb-5 drop-shadow-2xl">
            Tired of Slow Internet & Endless Buffering?
          </h2>
          <p className="text-base text-pink-100 mb-8">
            Switch to Netix Fibernet today and enjoy lightning-fast speeds, zero downtime, and 24/7 expert support. 
            Over 1000+ happy customers trust us for their home and business internet needs.
          </p>
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4 justify-center">
            <Button
              onClick={() => navigate('/request-service')}
              className="h-14 px-8 text-base font-black bg-white text-red-700 hover:bg-red-50 shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 border-2 border-white/50 group"
            >
              Get Free Quote - Order Now
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              onClick={() => window.open('tel:+919319919988')}
              data-testid="home-call-mobile-button"
              variant="outline"
              className="h-14 px-8 text-base font-black border-2 border-white text-white hover:bg-white hover:text-red-700 shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 backdrop-blur-sm"
            >
              <Phone className="mr-2 h-5 w-5" />
              Call: +91 93199 19988
            </Button>
            <Button
              onClick={() => window.open('tel:06276459015')}
              data-testid="home-call-office-button"
              variant="outline"
              className="h-14 px-8 text-base font-black border-2 border-white text-white hover:bg-white hover:text-red-700 shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 backdrop-blur-sm"
            >
              <Phone className="mr-2 h-5 w-5" />
              Office: 06276 - 459015
            </Button>
          </div>
        </div>
      </section>

      {/* SEO Content Section - Pain Points & Solutions */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          {/* Pain Points Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {/* Pain Point 1 */}
            <Card className="border-2 border-red-100 hover:border-red-300 transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                    <WifiOff className="h-6 w-6 text-red-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800">Frustrated with Slow Speeds?</h3>
                </div>
                <p className="text-gray-600 text-sm mb-4">
                  Say goodbye to buffering and lagging! Our fiber-optic technology delivers consistent speeds up to 
                  <strong> 1 Gbps</strong> - perfect for 4K streaming, online gaming, and work from home.
                </p>
                <div className="flex items-center text-green-600 font-semibold text-sm">
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Netix Solution: True Gigabit Fiber Internet
                </div>
              </CardContent>
            </Card>

            {/* Pain Point 2 */}
            <Card className="border-2 border-red-100 hover:border-red-300 transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                    <AlertCircle className="h-6 w-6 text-orange-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800">Tired of Frequent Downtime?</h3>
                </div>
                <p className="text-gray-600 text-sm mb-4">
                  Internet outages costing you productivity? Netix Fibernet guarantees <strong>99.9% uptime</strong> with 
                  redundant infrastructure and proactive monitoring - your connection stays stable 24/7.
                </p>
                <div className="flex items-center text-green-600 font-semibold text-sm">
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Netix Solution: SLA-Backed Uptime Guarantee
                </div>
              </CardContent>
            </Card>

            {/* Pain Point 3 */}
            <Card className="border-2 border-red-100 hover:border-red-300 transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                    <Phone className="h-6 w-6 text-yellow-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800">Bad Customer Support?</h3>
                </div>
                <p className="text-gray-600 text-sm mb-4">
                  Waiting days for someone to fix your internet? Our <strong>24/7 local support team</strong> resolves 
                  most issues within 4 hours. Call, WhatsApp, or email - we're always here for you.
                </p>
                <div className="flex items-center text-green-600 font-semibold text-sm">
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Netix Solution: Round-the-Clock Expert Support
                </div>
              </CardContent>
            </Card>

            {/* Pain Point 4 */}
            <Card className="border-2 border-red-100 hover:border-red-300 transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <Clock className="h-6 w-6 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800">Slow Installation Process?</h3>
                </div>
                <p className="text-gray-600 text-sm mb-4">
                  Need internet urgently? We offer <strong>same-day or next-day installation</strong> in most areas. 
                  Our trained technicians complete setup in just 2-3 hours with FREE professional installation.
                </p>
                <div className="flex items-center text-green-600 font-semibold text-sm">
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Netix Solution: Rapid Free Installation
                </div>
              </CardContent>
            </Card>

            {/* Pain Point 5 */}
            <Card className="border-2 border-red-100 hover:border-red-300 transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800">Hidden Charges & FUP Limits?</h3>
                </div>
                <p className="text-gray-600 text-sm mb-4">
                  Other ISPs slow you down after hitting data caps? Netix offers <strong>truly unlimited data</strong> 
                  with no FUP limits, no throttling, and absolutely no hidden charges. What you see is what you pay.
                </p>
                <div className="flex items-center text-green-600 font-semibold text-sm">
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Netix Solution: Transparent Unlimited Plans
                </div>
              </CardContent>
            </Card>

            {/* Pain Point 6 */}
            <Card className="border-2 border-red-100 hover:border-red-300 transition-all hover:shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <Tv className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800">Buffering During Streaming?</h3>
                </div>
                <p className="text-gray-600 text-sm mb-4">
                  Netflix, Prime, Hotstar constantly buffering? Our optimized network ensures <strong>seamless 4K streaming</strong> 
                  on multiple devices simultaneously. Enjoy uninterrupted entertainment every time.
                </p>
                <div className="flex items-center text-green-600 font-semibold text-sm">
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Netix Solution: OTT-Optimized Connection
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Comparison Section */}
          <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-2xl p-8 mb-12">
            <h3 className="text-2xl font-bold text-center mb-6 text-gray-800">
              Why Customers Switch to Netix Fibernet
            </h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-lg font-bold text-red-600 mb-4 flex items-center">
                  <X className="h-5 w-5 mr-2" />
                  Traditional ISP Problems
                </h4>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2 text-gray-600">
                    <X className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span>Slow speeds during peak hours</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-600">
                    <X className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span>Frequent disconnections and outages</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-600">
                    <X className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span>Poor customer support response</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-600">
                    <X className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span>Hidden charges and FUP limits</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-600">
                    <X className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span>Weeks-long installation wait</span>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="text-lg font-bold text-green-600 mb-4 flex items-center">
                  <CheckCircle2 className="h-5 w-5 mr-2" />
                  Netix Fibernet Advantage
                </h4>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2 text-gray-600">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Consistent speeds 24/7, up to 1 Gbps</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-600">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>99.9% uptime with SLA guarantee</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-600">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>24/7 local support, 4-hour resolution</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-600">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Truly unlimited - no hidden costs</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-600">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Same-day FREE installation</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Trust Signals Section - Compact & Professional */}
      <section className="py-16 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
        {/* Subtle background */}
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-red-100/40 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-blue-100/40 rounded-full blur-3xl"></div>
        
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="text-center mb-10">
            <div className="inline-block mb-3 px-4 py-1.5 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full text-white font-bold text-xs shadow-md">
              <BadgeCheck className="inline h-4 w-4 mr-1" />
              Trusted & Verified ISP
            </div>
            <h2 className="text-3xl font-black text-gray-900 mb-2">
              Why 1000+ Customers Trust Netix Fibernet
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto text-sm">
              Years of experience serving homes and businesses across India with reliability and transparency.
            </p>
          </div>
          
          {/* Compact Trust Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Card 1 - Happy Customers */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-red-400 to-rose-500 rounded-2xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
              <div className="relative bg-white rounded-2xl p-5 shadow-lg border border-gray-100 hover:shadow-xl hover:border-red-200 transition-all duration-300 hover:-translate-y-1">
                <div className="w-12 h-12 mx-auto mb-3 bg-gradient-to-br from-red-500 to-rose-600 rounded-xl flex items-center justify-center shadow-lg shadow-red-500/30">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <div className="text-3xl font-black text-red-600 text-center">1000+</div>
                <div className="text-gray-800 font-semibold text-sm text-center">Happy Customers</div>
                <div className="text-xs text-gray-400 text-center">Homes & Businesses</div>
              </div>
            </div>

            {/* Card 2 - Uptime Guarantee */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 to-green-500 rounded-2xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
              <div className="relative bg-white rounded-2xl p-5 shadow-lg border border-gray-100 hover:shadow-xl hover:border-green-200 transition-all duration-300 hover:-translate-y-1">
                <div className="w-12 h-12 mx-auto mb-3 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg shadow-green-500/30">
                  <Shield className="h-6 w-6 text-white" />
                </div>
                <div className="text-3xl font-black text-green-600 text-center">99.9%</div>
                <div className="text-gray-800 font-semibold text-sm text-center">Uptime Guarantee</div>
                <div className="text-xs text-gray-400 text-center">SLA Backed</div>
              </div>
            </div>

            {/* Card 3 - Customer Rating */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
              <div className="relative bg-white rounded-2xl p-5 shadow-lg border border-gray-100 hover:shadow-xl hover:border-amber-200 transition-all duration-300 hover:-translate-y-1">
                <div className="w-12 h-12 mx-auto mb-3 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/30">
                  <Star className="h-6 w-6 text-white fill-white" />
                </div>
                <div className="text-3xl font-black text-amber-600 text-center">4.8/5</div>
                <div className="text-gray-800 font-semibold text-sm text-center">Customer Rating</div>
                <div className="flex justify-center gap-0.5 mt-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-3 w-3 text-amber-400 fill-amber-400" />
                  ))}
                </div>
              </div>
            </div>

            {/* Card 4 - Expert Support */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-2xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
              <div className="relative bg-white rounded-2xl p-5 shadow-lg border border-gray-100 hover:shadow-xl hover:border-blue-200 transition-all duration-300 hover:-translate-y-1">
                <div className="w-12 h-12 mx-auto mb-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30">
                  <Headphones className="h-6 w-6 text-white" />
                </div>
                <div className="text-3xl font-black text-blue-600 text-center">24/7</div>
                <div className="text-gray-800 font-semibold text-sm text-center">Expert Support</div>
                <div className="text-xs text-gray-400 text-center">Local Teams</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Testimonials - Social Proof */}
      <section className="py-16 bg-gradient-to-br from-red-900 via-pink-900 to-blue-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${1 + Math.random() * 98}%`,
                animationDelay: `${Math.random() * 3}s`
              }}
            />
          ))}
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-block mb-3 px-4 py-1.5 bg-white/20 backdrop-blur-sm rounded-full text-white font-bold text-xs border border-white/30">
              <ThumbsUp className="inline h-4 w-4 mr-1" />
              Real Customer Reviews
            </div>
            <h2 className="text-3xl font-black text-white mb-3">
              What Our Customers Say
            </h2>
            <p className="text-pink-100 max-w-2xl mx-auto">
              Don't just take our word for it. Here's what real customers from across India have to say about their Netix Fibernet experience.
            </p>
          </div>

          {/* Testimonials Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {/* Testimonial 1 */}
            <Card className="border-0 bg-white/10 backdrop-blur-sm hover:bg-white/15 transition-all">
              <CardContent className="p-6">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <Quote className="h-8 w-8 text-red-400 mb-3 opacity-50" />
                <p className="text-white/90 text-sm mb-4 leading-relaxed">
                  "Finally found an ISP that delivers what it promises! Switched from a major provider after years of slow speeds during peak hours. With Netix, I consistently get <strong>200 Mbps</strong> - streaming Netflix in 4K while my kids game online, no issues!"
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-white/20">
                  <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold">
                    RS
                  </div>
                  <div>
                    <div className="text-white font-bold text-sm">Rahul Sharma</div>
                    <div className="text-pink-200 text-xs flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> Bihar • Home User
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Testimonial 2 */}
            <Card className="border-0 bg-white/10 backdrop-blur-sm hover:bg-white/15 transition-all">
              <CardContent className="p-6">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <Quote className="h-8 w-8 text-red-400 mb-3 opacity-50" />
                <p className="text-white/90 text-sm mb-4 leading-relaxed">
                  "Running a business requires reliable internet. Previous ISP had constant outages affecting our operations. Netix's <strong>99.9% uptime</strong> has been a game-changer. Their <strong>24/7 support</strong> actually responds within minutes!"
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-white/20">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                    PP
                  </div>
                  <div>
                    <div className="text-white font-bold text-sm">Priya Patel</div>
                    <div className="text-pink-200 text-xs flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> Gujarat • Business Owner
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Testimonial 3 */}
            <Card className="border-0 bg-white/10 backdrop-blur-sm hover:bg-white/15 transition-all">
              <CardContent className="p-6">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <Quote className="h-8 w-8 text-red-400 mb-3 opacity-50" />
                <p className="text-white/90 text-sm mb-4 leading-relaxed">
                  "Work from home requires stable connection for video calls. Old ISP caused embarrassing disconnections during client meetings. Since switching to Netix, <strong>zero buffering</strong> on Zoom, Teams, or Meet. Installation was done <strong>same day</strong>!"
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-white/20">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-teal-500 rounded-full flex items-center justify-center text-white font-bold">
                    AK
                  </div>
                  <div>
                    <div className="text-white font-bold text-sm">Amit Kumar</div>
                    <div className="text-pink-200 text-xs flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> Delhi NCR • IT Professional
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* More Testimonials Row */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Testimonial 4 */}
            <Card className="border-0 bg-white/10 backdrop-blur-sm hover:bg-white/15 transition-all">
              <CardContent className="p-6">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-white/90 text-sm mb-4 leading-relaxed">
                  "As a gamer, ping matters. Other ISPs gave 80-100ms to Mumbai servers. Netix delivers <strong>consistent 20-30ms ping</strong>. No more lag deaths in PUBG or Valorant! Plus their plans are actually affordable compared to competitors."
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-white/20">
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center text-white font-bold">
                    VR
                  </div>
                  <div>
                    <div className="text-white font-bold text-sm">Vikram Rao</div>
                    <div className="text-pink-200 text-xs flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> Maharashtra • Gamer & Streamer
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Testimonial 5 */}
            <Card className="border-0 bg-white/10 backdrop-blur-sm hover:bg-white/15 transition-all">
              <CardContent className="p-6">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-white/90 text-sm mb-4 leading-relaxed">
                  "We run a hotel and needed reliable WiFi for 50+ rooms. Netix provided a custom <strong>business solution</strong> with dedicated bandwidth. Guest complaints about internet dropped to zero. Their enterprise support is exceptional!"
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-white/20">
                  <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                    SM
                  </div>
                  <div>
                    <div className="text-white font-bold text-sm">Sunita Mehta</div>
                    <div className="text-pink-200 text-xs flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> Rajasthan • Hotel Owner
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* CTA after testimonials */}
          <div className="text-center mt-10">
            <p className="text-pink-100 mb-4">Join thousands of satisfied customers across India</p>
            <Button
              onClick={() => navigate('/request-service')}
              className="h-12 px-8 font-bold bg-white text-red-600 hover:bg-red-50"
            >
              Get Your Connection Today
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Internal Links Section - SEO Optimized */}
      <section className="py-12 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8">
            <h3 className="text-xl font-bold text-gray-800 mb-2">Explore Our Services</h3>
            <p className="text-gray-500 text-sm">Quick links to help you find what you need</p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            {/* Quick Link 1 */}
            <div 
              onClick={() => navigate('/request-service')}
              className="group cursor-pointer bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-all hover:border-red-200 border border-gray-100"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center group-hover:bg-red-200 transition-colors">
                  <Zap className="h-5 w-5 text-red-600" />
                </div>
                <h4 className="font-bold text-gray-800 group-hover:text-red-600 transition-colors">Get New Connection</h4>
              </div>
              <p className="text-xs text-gray-500">Order high-speed fiber internet for your home or business</p>
            </div>

            {/* Quick Link 2 */}
            <div 
              onClick={() => navigate('/coverage-area')}
              className="group cursor-pointer bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-all hover:border-blue-200 border border-gray-100"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                  <MapPin className="h-5 w-5 text-blue-600" />
                </div>
                <h4 className="font-bold text-gray-800 group-hover:text-blue-600 transition-colors">Check Coverage</h4>
              </div>
              <p className="text-xs text-gray-500">See if fiber internet is available in your area</p>
            </div>

            {/* Quick Link 3 */}
            <div 
              onClick={() => navigate('/speed-test')}
              className="group cursor-pointer bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-all hover:border-green-200 border border-gray-100"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 transition-colors">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                </div>
                <h4 className="font-bold text-gray-800 group-hover:text-green-600 transition-colors">Speed Test</h4>
              </div>
              <p className="text-xs text-gray-500">Test your current internet speed for free</p>
            </div>

            {/* Quick Link 4 */}
            <div 
              onClick={() => navigate('/about')}
              className="group cursor-pointer bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-all hover:border-purple-200 border border-gray-100"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                  <Building className="h-5 w-5 text-purple-600" />
                </div>
                <h4 className="font-bold text-gray-800 group-hover:text-purple-600 transition-colors">About Netix</h4>
              </div>
              <p className="text-xs text-gray-500">Learn about our mission, team, and infrastructure</p>
            </div>
          </div>

          {/* Additional Links Row */}
          <div className="flex flex-wrap justify-center gap-4 mt-8 text-sm">
            <span 
              onClick={() => navigate('/career')}
              className="cursor-pointer text-gray-600 hover:text-red-600 transition-colors"
            >
              Career Opportunities
            </span>
            <span className="text-gray-300">|</span>
            <span 
              onClick={() => navigate('/privacy-policy')}
              className="cursor-pointer text-gray-600 hover:text-red-600 transition-colors"
            >
              Privacy Policy
            </span>
            <span className="text-gray-300">|</span>
            <span 
              onClick={() => navigate('/terms-and-conditions')}
              className="cursor-pointer text-gray-600 hover:text-red-600 transition-colors"
            >
              Terms & Conditions
            </span>
            <span className="text-gray-300">|</span>
            <span 
              onClick={() => navigate('/refund-return-policy')}
              className="cursor-pointer text-gray-600 hover:text-red-600 transition-colors"
            >
              Refund Policy
            </span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
